// Number keys open boards (Features 2, 21 Oct): 1 opens the first board in the boards list, 2 the second, and so on
// up to 9, in the list's order as the drop-down shows it, with a folded folder's boards left out. On unless turned
// off in Settings, Keyboard. Both the switch and which folders are folded are kept in this browser, so the numbers
// always match the list this browser shows.

import { create } from "zustand";
import { keyedBoards } from "../../shared/boardsOrder";
import type { Board } from "../../shared/types";
import { isTyping } from "../lib/util";
import { codeShows, sharedShows, type AppState } from "./store";
import { readStoredJson, writeStoredJson } from "../lib/stored";

const KEYS = "cb:keyboard";
const OPENED = "cb:folders:open";

export interface KeyPrefs {
  /** 1 to 9 open the boards list's boards */
  numbers: boolean;
}

function readKeys(): KeyPrefs {
  const v = readStoredJson<Partial<KeyPrefs> | null>(KEYS, null);
  return { numbers: v?.numbers !== false };
}

export const useKeyPrefs = create<KeyPrefs>(readKeys);

export function setKeyPrefs(patch: Partial<KeyPrefs>) {
  useKeyPrefs.setState(patch);
  writeStoredJson(KEYS, useKeyPrefs.getState()); // private mode: kept for this visit
}

/** Which folders this browser keeps folded: each folder is open until folded, and stays as it was left. */
function readOpened(): Record<string, boolean> {
  const v = readStoredJson<unknown>(OPENED, {});
  return v && typeof v === "object" ? (v as Record<string, boolean>) : {};
}

export const useFoldersOpen = create<{ opened: Record<string, boolean> }>(() => ({ opened: readOpened() }));

export const folderIsOpen = (opened: Record<string, boolean>, id: string) => opened[id] !== false;

// another tab folding a folder: this one follows, so the two never write over each other's folds (review, 21 Oct)
if (typeof window !== "undefined")
  window.addEventListener("storage", (e) => {
    if (e.key === OPENED) useFoldersOpen.setState({ opened: readOpened() });
  });

export function setFolderOpen(id: string, open: boolean) {
  // from what this browser holds now, which another tab may have changed
  const opened = { ...useFoldersOpen.getState().opened, ...readOpened(), [id]: open };
  useFoldersOpen.setState({ opened });
  writeStoredJson(OPENED, opened); // out of reach: kept for this visit
}

/** The boards 1 to 9 open, in order, as the list shows them now. */
export function numberedBoards(s: Pick<AppState, "boards" | "workspace">, opened = useFoldersOpen.getState().opened): Board[] {
  return keyedBoards(s.workspace?.folders ?? [], s.boards, (id) => folderIsOpen(opened, id));
}

type KeyLike = Pick<KeyboardEvent, "key" | "altKey" | "ctrlKey" | "metaKey" | "target">;

/**
 * The board a key opens, or null when it opens none. A plain 1 to 9 only: with Ctrl, ⌘ or Alt the key is the
 * browser's. Never while typing (a box, a page, a note, the chat), never with a panel, a dialog, a menu or the theme
 * strip open over the board, never in full screen (one board, on purpose, as All boards is not), on a phone or on a
 * board someone shared, and not once it is turned off.
 */
export function boardForKey(s: AppState, e: KeyLike): Board | null {
  if (!/^[1-9]$/.test(e.key) || e.ctrlKey || e.metaKey || e.altKey) return null;
  if (!useKeyPrefs.getState().numbers || isTyping(e)) return null;
  if (s.mobile || s.guest || s.presenting) return null;
  if (s.panel || s.themePicker || codeShows(s) || sharedShows(s)) return null;
  if (typeof document !== "undefined" && document.querySelector?.(".dialog-scrim")) return null;
  const t = e.target as Element | null;
  if (t?.closest?.("[role=menu], [data-esc-own]")) return null;
  return numberedBoards(s)[Number(e.key) - 1] ?? null;
}
